import { Scene, type Vector3 } from '@babylonjs/core';
import { type PhysicsWorld } from '@roadworld/physics';
import type { ChunkManager } from '../world/ChunkManager.js';
import { chunkKey, type ChunkCoord } from '../world/coords.js';
import type { GraphEdge, RoadGraph } from './roadGraph.js';
import { laneOffset } from './steering.js';
import { TrafficCar } from './TrafficCar.js';
import { pickBodyColor, spawnWeight, targetSpeedMs, TrafficTuning } from './tuning.js';

/** Snapshot accessor for the player's world position. Used at spawn time to reject slots
 *  that would drop a car on top of the player. */
export type PlayerPositionProvider = () => Vector3 | null;

/**
 * Per-frame context passed into each TrafficCar.update(). Gives cars access to global
 * services (intersection occupancy, current time) without each car holding a back-reference
 * to the manager.
 */
export interface TrafficContext {
  nowSec: number;
  /** Try to claim an intersection. Returns 'go' if the car holds (or just took) the
   *  reservation, 'yield' if another car holds it. */
  reserve(carId: number, nodeId: number): 'go' | 'yield';
  /** Release a reservation. No-op if the car doesn't hold it (e.g. it expired). */
  release(carId: number, nodeId: number): void;
}

/**
 * Orchestrator for AI traffic. Hooks ChunkManager's chunk-ready/dispose callbacks; spawns
 * cars on a newly-ready chunk and despawns them when the chunk goes out of cache range.
 *
 * Per-frame: parent calls `update(dt)` after physics.step, which ticks every live car and
 * reaps any that have flagged `shouldDespawn`.
 *
 * Slice 2: cars are spawned on a uniformly-random graph edge with the right initial heading,
 * then follow the directed road graph (including cross-chunk handoff via OSM nodeIds).
 * Slice 3 will replace the spawn-count + edge-pick with class-weighted density.
 */
export class TrafficManager {
  private scene: Scene;
  private physics: PhysicsWorld;
  private chunks: ChunkManager;
  private byChunk = new Map<string, TrafficCar[]>();
  private nextCarId = 1;
  private rng: () => number = Math.random;
  private enabled = false;
  /** Master switch for distance-based traffic LOD. Consumed by per-car visual updates
   *  (Task #4). Stored here so the HUD toggle has somewhere to land today. */
  private lodEnabled = true;
  private playerPosProvider: PlayerPositionProvider | null = null;
  /** Global intersection occupancy. Keyed by OSM nodeId — unique across all chunks, so an
   *  intersection that straddles a chunk border gets ONE lock, not two. */
  private intersections = new Map<number, { occupantId: number; reservedUntil: number }>();
  /** Accumulated session time (sec) used for reservation timestamps. */
  private nowSec = 0;

  constructor(scene: Scene, physics: PhysicsWorld, chunks: ChunkManager) {
    this.scene = scene;
    this.physics = physics;
    this.chunks = chunks;
  }

  /** Optional — if set, spawn placement rejects slots within `minSpawnSeparation` of the
   *  player. Without it, the only separation enforced is from already-spawned cars. */
  setPlayerPositionProvider(p: PlayerPositionProvider): void {
    this.playerPosProvider = p;
  }

  setLodEnabled(enabled: boolean): void {
    this.lodEnabled = enabled;
  }

  isLodEnabled(): boolean {
    return this.lodEnabled;
  }

  /** Wire callbacks. Idempotent. */
  setEnabled(on: boolean): void {
    if (this.enabled === on) return;
    this.enabled = on;
    if (on) {
      this.chunks.setOnChunkReady((c) => this.onChunkReady(c));
      this.chunks.setOnChunkDispose((c) => this.onChunkDispose(c));
    } else {
      this.disposeAll();
    }
  }

  carCount(): number {
    let total = 0;
    for (const arr of this.byChunk.values()) total += arr.length;
    return total;
  }

  /** World-space positions of every live car. Used by the minimap to draw traffic dots. */
  getCarPositions(): Array<{ x: number; z: number }> {
    const out: Array<{ x: number; z: number }> = [];
    for (const arr of this.byChunk.values()) {
      for (const car of arr) {
        const p = car.position();
        out.push({ x: p.x, z: p.z });
      }
    }
    return out;
  }

  /** World-space pose of every live car, used by the headlight system to position SpotLights. */
  getCarsForHeadlights(): Array<{ pos: Vector3; yaw: number }> {
    const out: Array<{ pos: Vector3; yaw: number }> = [];
    for (const arr of this.byChunk.values()) {
      for (const car of arr) {
        out.push({ pos: car.position(), yaw: car.getYaw() });
      }
    }
    return out;
  }

  /** Step + reap. Call AFTER physics.step. */
  update(dt: number): void {
    if (!this.enabled) return;
    this.nowSec += dt;
    const ctx: TrafficContext = {
      nowSec: this.nowSec,
      reserve: (carId, nodeId) => this.reserveIntersection(carId, nodeId),
      release: (carId, nodeId) => this.releaseIntersection(carId, nodeId),
    };
    for (const [key, arr] of this.byChunk) {
      // Iterate in place; reap dead cars after.
      for (const car of arr) car.update(dt, ctx);
      // Reap.
      let writeIdx = 0;
      for (let i = 0; i < arr.length; i++) {
        const car = arr[i]!;
        if (car.shouldDespawn) {
          car.dispose();
        } else {
          arr[writeIdx++] = car;
        }
      }
      arr.length = writeIdx;
      if (arr.length === 0) {
        // Don't delete the key — chunk-dispose handles that. Empty array is fine.
        // But if the chunk has been disposed (key removed from chunks), tidy up.
        if (!this.chunks.getChunkByKey(key)) this.byChunk.delete(key);
      }
    }
  }

  /** Sync visuals. Call after `update`, before `scene.render`. */
  /** Push physics-stepped positions to visuals. If `cameraPos` is provided, also applies
   *  distance-based wheel LOD per car so the per-car draw-call count drops with distance. */
  syncVisuals(cameraPos?: Vector3): void {
    if (!this.enabled) return;
    const camX = cameraPos?.x ?? 0;
    const camZ = cameraPos?.z ?? 0;
    const applyLod = cameraPos !== undefined;
    const lodOn = this.lodEnabled;
    for (const arr of this.byChunk.values()) {
      for (const car of arr) {
        car.syncVisual();
        if (applyLod) car.updateVisualLOD(camX, camZ, lodOn);
      }
    }
  }

  // -------- internals --------

  private onChunkReady(coord: ChunkCoord): void {
    const key = chunkKey(coord);
    if (this.byChunk.has(key)) return;

    const chunk = this.chunks.getChunkByKey(key);
    if (!chunk) return;
    const graph = chunk.getRoadGraph();
    if (!graph || graph.edges.length === 0) {
      this.byChunk.set(key, []);
      return;
    }

    const arr: TrafficCar[] = [];
    this.byChunk.set(key, arr);

    // Density = budget × (chunk's road weight / reference weight), clamped to [0, cap].
    // Chunks with a motorway crossing them get more cars; pure-residential chunks get fewer.
    const T = TrafficTuning;
    const chunkWeight = computeChunkRoadWeight(graph);
    const targetCount = clampInt(
      Math.round(T.carsPerActiveChunkBudget * (chunkWeight / T.referenceChunkWeight)),
      0,
      T.maxCarsPerChunk,
    );
    if (targetCount === 0) return;

    // Build a cumulative-weight array over edges (each weight = length × class spawn weight).
    // Re-rolled per car so distribution is fresh each pick.
    const playerPos = this.playerPosProvider?.() ?? null;
    for (let i = 0; i < targetCount; i++) {
      let placed = false;
      for (let attempt = 0; attempt < T.maxSpawnAttempts; attempt++) {
        const edge = pickRandomEdge(graph, this.rng);
        if (!edge) break;
        const t = this.rng();
        const px = edge.x0 + (edge.x1 - edge.x0) * t;
        const pz = edge.z0 + (edge.z1 - edge.z0) * t;

        // Reject too close to player.
        if (playerPos) {
          const dx = px - playerPos.x;
          const dz = pz - playerPos.z;
          if (dx * dx + dz * dz < T.minSpawnSeparation * T.minSpawnSeparation) continue;
        }

        // Reject too close to already-spawned cars in this chunk.
        let conflict = false;
        const minSepSq = T.minSpawnSeparation * T.minSpawnSeparation;
        for (const existing of arr) {
          const ep = existing.position();
          const dx = px - ep.x;
          const dz = pz - ep.z;
          if (dx * dx + dz * dz < minSepSq) {
            conflict = true;
            break;
          }
        }
        if (conflict) continue;

        const car = this.spawnCarOnEdge(graph, edge, t);
        if (car) {
          arr.push(car);
          placed = true;
          break;
        }
      }
      if (!placed) {
        // Couldn't find a clear slot after maxSpawnAttempts — skip this car. The chunk
        // ends up under its target density, which is fine; the player isn't being
        // measured against a quota.
      }
    }
  }

  private spawnCarOnEdge(graph: RoadGraph, edge: GraphEdge, tParam: number): TrafficCar | null {
    // Position = lerp along the edge + lane offset so the car spawns on the correct side
    // of the road, not on the centerline. pursuitTarget applies the same offset to its
    // lookahead points, so the steering target and spawn pose agree.
    const baseX = edge.x0 + (edge.x1 - edge.x0) * tParam;
    const baseZ = edge.z0 + (edge.z1 - edge.z0) * tParam;
    const off = laneOffset(edge.tx, edge.tz, edge.halfWidth, edge.oneway);
    const px = baseX + off.x;
    const pz = baseZ + off.z;
    const groundY = this.chunks.sampleHeight(px, pz);
    const y = (groundY ?? 0) + TrafficTuning.chassisHeight * 0.5 + 0.1;

    // Yaw = direction of the edge tangent. atan2(x, z) gives our yaw convention.
    const yaw = Math.atan2(edge.tx, edge.tz);

    const car = new TrafficCar(
      this.physics,
      this.scene,
      this.chunks,
      {
        id: this.nextCarId++,
        x: px,
        y,
        z: pz,
        yaw,
        targetSpeedMs: targetSpeedMs(edge.roadClass, this.rng),
        bodyColor: pickBodyColor(this.rng),
        graph,
        edgeId: edge.id,
        tParam,
      },
      this.rng,
    );
    return car;
  }

  private onChunkDispose(coord: ChunkCoord): void {
    const key = chunkKey(coord);
    const arr = this.byChunk.get(key);
    if (!arr) return;
    for (const car of arr) car.dispose();
    this.byChunk.delete(key);
  }

  private disposeAll(): void {
    for (const arr of this.byChunk.values()) {
      for (const car of arr) car.dispose();
    }
    this.byChunk.clear();
    this.intersections.clear();
  }

  /**
   * Try to claim an intersection. Single-occupancy with a 2-second expiry — the timeout is
   * a deadlock release valve, in case a car gets stuck IN the intersection (collided with
   * the player and lost forward momentum, say). Without the timeout that intersection
   * would be permanently locked.
   */
  private reserveIntersection(carId: number, nodeId: number): 'go' | 'yield' {
    const lock = this.intersections.get(nodeId);
    if (!lock || lock.occupantId === carId || this.nowSec > lock.reservedUntil) {
      this.intersections.set(nodeId, {
        occupantId: carId,
        reservedUntil: this.nowSec + TrafficTuning.intersectionReservationSec,
      });
      return 'go';
    }
    return 'yield';
  }

  private releaseIntersection(carId: number, nodeId: number): void {
    const lock = this.intersections.get(nodeId);
    if (lock?.occupantId === carId) this.intersections.delete(nodeId);
  }
}

/** Class+length-weighted reservoir pick over edges. Highways dominate purely by their
 *  spawnWeight; longer edges of the same class get proportionally more picks. */
function pickRandomEdge(graph: RoadGraph, rng: () => number): GraphEdge | null {
  if (graph.edges.length === 0) return null;
  let totalW = 0;
  let picked: GraphEdge | null = null;
  for (const e of graph.edges) {
    const w = spawnWeight(e.roadClass) * e.length;
    if (w <= 0) continue;
    totalW += w;
    if (rng() * totalW < w) picked = e;
  }
  return picked;
}

/** Sum (length × class spawn weight) across all edges in a graph. Used to scale per-chunk
 *  density: highway-heavy chunks get more cars than residential-only chunks. */
function computeChunkRoadWeight(graph: RoadGraph): number {
  let total = 0;
  for (const e of graph.edges) total += e.length * spawnWeight(e.roadClass);
  return total;
}

function clampInt(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
